import { TaskViewLayoutDTO } from 'models/dto/tasks/taskViewDTO';
import { getInitialHiddenTemplateViewKeys } from './TaskViewHelpers';
import { ITaskViewLayoutState, TaskViewFilterMap } from 'components/Tasks/TaskViews/TaskViewTypes';

export const taskViewLayoutVersion = 1;

const isPersistableViewKey = (key: string): boolean => !key.startsWith('view-creating-');

const copyPersistableStringArray = (values: string[] | undefined): string[] => {
  return [...new Set((values ?? []).filter(isPersistableViewKey))];
};

const copyFilterMap = (filters: TaskViewFilterMap | undefined, keepEmptyEntries: boolean): TaskViewFilterMap => {
  if (!filters) return {};

  return Object.keys(filters).reduce<TaskViewFilterMap>((result, key) => {
    if (!isPersistableViewKey(key)) return result;
    const values = filters[key] ?? [];
    if (keepEmptyEntries || values.length > 0) {
      result[key] = [...values];
    }

    return result;
  }, {});
};

export const createTaskViewLayoutState = (): ITaskViewLayoutState => ({
  viewOrder: [],
  hiddenTemplateViewKeys: getInitialHiddenTemplateViewKeys(undefined, undefined),
  hiddenCustomViewKeys: [],
  hiddenSidebarViewKeys: [],
  viewMemberFilters: {},
  standardViewFilterOverrides: {},
});

export const readTaskViewLayout = (layout: TaskViewLayoutDTO | undefined): ITaskViewLayoutState => {
  const viewOrder = copyPersistableStringArray(layout?.viewOrder);
  const persistedHiddenTemplateViewKeys = layout?.hiddenTemplateViewKeys === undefined
    ? undefined
    : copyPersistableStringArray(layout.hiddenTemplateViewKeys);

  return {
    viewOrder,
    hiddenTemplateViewKeys: getInitialHiddenTemplateViewKeys(persistedHiddenTemplateViewKeys, viewOrder),
    hiddenCustomViewKeys: copyPersistableStringArray(layout?.hiddenCustomViewKeys),
    hiddenSidebarViewKeys: copyPersistableStringArray(layout?.hiddenSidebarViewKeys),
    viewMemberFilters: copyFilterMap(layout?.viewMemberFilters, false),
    standardViewFilterOverrides: copyFilterMap(layout?.standardViewFilterOverrides, true),
  };
};

export const writeTaskViewLayout = (
  layout: ITaskViewLayoutState,
  revision: string | undefined,
): TaskViewLayoutDTO => ({
  ...(revision ? { revision } : {}),
  version: taskViewLayoutVersion,
  viewOrder: copyPersistableStringArray(layout.viewOrder),
  hiddenTemplateViewKeys: copyPersistableStringArray(layout.hiddenTemplateViewKeys),
  hiddenCustomViewKeys: copyPersistableStringArray(layout.hiddenCustomViewKeys),
  hiddenSidebarViewKeys: copyPersistableStringArray(layout.hiddenSidebarViewKeys),
  viewMemberFilters: copyFilterMap(layout.viewMemberFilters, false),
  standardViewFilterOverrides: copyFilterMap(layout.standardViewFilterOverrides, true),
});

// The server sends "0" while the user has no stored layout yet.
export const getTaskViewLayoutRevision = (revision: string | undefined): string | undefined =>
  revision && revision !== '0' ? revision : undefined;

export const hasTaskViewLayoutContent = (layout: TaskViewLayoutDTO | undefined): boolean => {
  return Boolean(
    layout?.viewOrder?.length ||
    layout?.hiddenTemplateViewKeys?.length ||
    layout?.hiddenCustomViewKeys?.length ||
    layout?.hiddenSidebarViewKeys?.length ||
    Object.keys(layout?.viewMemberFilters ?? {}).length ||
    Object.keys(layout?.standardViewFilterOverrides ?? {}).length,
  );
};
